/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, IconButton, makeStyles, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import { SelectedItemProps } from '../MattersByStatusChart';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { SearchCriteria } from '../SearchCriteria';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { DrillDownType } from '../DrillDownType';
import MTooltip from '@material-ui/core/Tooltip';
import { ChartInfo } from '../../components/ChartInfo';
import { VideoModal } from '../../components/VideoModal';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';

import { RV_CHART_CLASS, RV_CHART_COLORS, rvBarDatasetStyle, rvChartAnimation, rvValueAxisStyle, rvCategoryAxisStyle, rvTooltipStyle, rvLegendLabels, isChartDataEmpty } from '../chartTheme';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartHeading } from '../ChartHeading';
import { ChartLimitNote } from '../ChartLimitNote';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../AnimatedNumber';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: '15px 15px 0px 15px',
            fontSize: '16px',
            fontWeight: 600,
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            [`& .${`titleWrapper`}`]: {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                [`& .${`title`}`]: {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`value`}`]: {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`secondary-title`}`]: {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
            '&:hover': {
                '& .scroll': {
                    display: 'flex !important',
                    '& .Mui-disabled': {
                        '& .MuiSvgIcon-root': {
                            color: 'inherit !important'
                        }
                    }
                }
            }
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative'
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        footer: {
            fontSize: '1rem',
            textAlign: 'center',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        span: {
            fontSize: '1rem',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        scrollUp: {
            position: 'absolute',
            top: '30px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        scrollDown: {
            position: 'absolute',
            bottom: '90px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)'
        }
    }),
);

interface ClientExposureChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

interface ClientExposureData {
    statistics: Statistics;
}

export interface Statistics {
    matter: Matter;
}

export interface Matter {
    clientExposure: ClientExposure;
}

export interface ClientExposure {
    exposure: Exposure[];
    groupedByCount: number;
    total: number;
}

export interface Exposure {
    debtors: number;
    unbilledFees: number;
    unbilledDisbursements: number;
    total: number;
    groupedBy: string;
    matters: number;
}

interface ChartState {
    total: number;
    groupedByCount: number;
    debtors: number[];
    unbilledFees: number[];
    unbilledDisbursements: number[];
    exposureTotal: number[];
    groupedBy: string[];
    matters: number[];
    symbol: string;
}
export const ClientExposureChart: React.FC<ClientExposureChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollUpAndPrevRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollDownAndNextRef = React.useRef<any>();

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    const { loading, error, data } = useQuery<ClientExposureData>(BILLED_VS_ESTIMATE_QUERY, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
            showAllRecords: props.showAllRecords,
        },
    });

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const [state, setState] = useState<ChartState>({
        total: 0,
        groupedByCount: 0,
        debtors: [],
        unbilledFees: [],
        unbilledDisbursements: [],
        exposureTotal: [],
        groupedBy: [],
        matters: [],
        symbol: '$',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        window.addEventListener('keydown', onKeyDownHandler);

        return () => {
            window.removeEventListener('keydown', onKeyDownHandler);
        };
        // tslint:disable-next-line: align
    }, []);

    // tslint:disable-next-line: no-any
    const onKeyDownHandler = (event: any) => {
        if (event.keyCode === 80 && (event.ctrlKey || event.metaKey) && !event.altKey) {
            event.preventDefault();
            if (event.stopImmediatePropagation) {
                event.stopImmediatePropagation();
            } else {
                event.stopPropagation();
            }
            return;
        }
    };

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let total: number = 0;
                let groupedByCount: number = 0;
                let debtors: number[] = [];
                let unbilledFees: number[] = [];
                let unbilledDisbursements: number[] = [];
                let exposureTotal: number[] = [];
                let groupedBy: string[] = [];
                let matters: number[] = [];

                if (!error && data && data.statistics && data.statistics.matter && data.statistics.matter.clientExposure) {
                    total = data.statistics.matter.clientExposure.total;
                    groupedByCount = data.statistics.matter.clientExposure.groupedByCount;

                    debtors = data.statistics.matter.clientExposure.exposure.map((o: Exposure) => {
                        return o.debtors;
                    });
                    unbilledFees = data.statistics.matter.clientExposure.exposure.map((o: Exposure) => {
                        return o.unbilledFees;
                    });
                    unbilledDisbursements = data.statistics.matter.clientExposure.exposure.map((o: Exposure) => {
                        return o.unbilledDisbursements;
                    });
                    exposureTotal = data.statistics.matter.clientExposure.exposure.map((o: Exposure) => {
                        return o.total;
                    });
                    groupedBy = data.statistics.matter.clientExposure.exposure.map((o: Exposure) => {
                        return o.groupedBy;
                    });
                    matters = data.statistics.matter.clientExposure.exposure.map((o: Exposure) => {
                        return o.matters;
                    });
                }

                return {
                    ...prevState,
                    total,
                    groupedByCount,
                    debtors,
                    unbilledFees,
                    unbilledDisbursements,
                    exposureTotal,
                    groupedBy,
                    matters,
                };
            });
        }
    // tslint:disable-next-line: align
    }, [loading, error, data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isClientExposureChartOpen');
        }
    };

    const onMaximize = () => {
        if (props.onMaximize) {
            props.onMaximize();
        }
    };

    const onScrollUpAndPrev = (type: 'up' | 'prev') => {
        if (chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min > 0) {
            const scrollRatio = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            const min = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            if (scrollRatio < chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min) {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= scrollRatio ? scrollRatio : 1;
            } else {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= min;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= min;
            }
            chartRef.current.options.scales[type === 'up' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll up
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll up
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }
        scrollDownAndNextRef.current.classList.remove('Mui-disabled');
    };

    const onScrollDownAndNext = (type: 'down' | 'next') => {
        const labels = state.groupedBy;

        if (chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max < labels.length) {
            const remainingBar = labels.length - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max;
            const scrollRatio = chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min;
            if (remainingBar <= scrollRatio) {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += remainingBar;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += remainingBar;
            } else {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += scrollRatio ? scrollRatio : 1;
            }
            chartRef.current.options.scales[type === 'down' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll down
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll down
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
        scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
    };

    const onScollBar = (type: 'up' | 'down' | 'prev' | 'next') => () => {
        if (type === 'up') {
            onScrollUpAndPrev('up');
        } else if (type === 'down') {
            onScrollDownAndNext('down');
        } else if (type === 'next') {
            onScrollDownAndNext('next');
        } else {
            onScrollUpAndPrev('prev');
        }
    };

    const onDisabledEnabledArrow = (barType: 'horizontal' | 'vertical') => {  
        const labels = state.groupedBy;
        // up 
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].min > 0) {
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }

        // down
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].max < labels.length) {
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
    };

    const onPan = (context: { chart: Chart }) => {
        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    const onZoom = (context: { chart: Chart }) => {
        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    // tslint:disable-next-line: no-any
    const onContextMenu = (event: any) => {
        event.preventDefault();
        if (event.button !== 2) {
            return false;
        }
        return true;
    };

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    // tslint:disable-next-line: no-any
    const onDownload = (name: string, extension: string = 'jpg') => (image: any) => {
        const a = document.createElement('a');
        a.href = image;
        a.download = createFileName(extension, name);
        a.click();
    };

    const onDownloadScreenshot = () => {
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload('Client Exposure'));
            // tslint:disable-next-line: align
        }, 500);
    };

    const chartControllerComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <div
                        className={`${classes.helpIcon} rv-chart-help`} 
                    >
                        <ChartInfo 
                            description={props.description}
                            fontSize={props.showAllRecords ? 'large' : 'medium'}
                        />
                        {props.infoUrl && (
                            <MTooltip title="Learn more">
                                <IconButton className={classes.learnIcon} aria-label="maximize" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                    <VideoLibraryOutlinedIcon />
                                </IconButton>
                            </MTooltip>
                        )}
                    </div>
                    {props.showAllRecords && (
                        <IconButton size="medium" onClick={onDownloadScreenshot}>
                            <GetAppOutlinedIcon color="primary" fontSize="large" />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximize && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon className={classes.closeIcon} fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    const handleTotalClick = () => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            searchCriterias.push({ property: 'Exposure', operator: 'gt', comparison: '0' });

            props.getSelectedItem(searchCriterias, false, 'Client Exposure', DrillDownType.matterSummaryDrilldown, 'Matters');
        }
    };

    if (loading || currencySymbolQuery.loading) {
        return (
            <div className="rv-chart rv-chart-loading">
                {chartControllerComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.debtors, state.unbilledFees, state.unbilledDisbursements) ? ' rv-chart--empty' : ''}`} ref={entireChartRef}>
            {isVideoModalOpen && props.infoUrl && (
                <VideoModal 
                    open={isVideoModalOpen}
                    onClose={onToggleVideo}
                    url={props.infoUrl}
                />
            )}
            {chartControllerComponent()}
            <div className={classes.titleContainer}>
                <ChartHeading icon={<PeopleOutlinedIcon />} title="Client Exposure" />
                <div
                    className={`${classes.total} h2`}
                    onClick={() => {
                        handleTotalClick();
                    }}
                >
                    <AnimatedNumber value={state.total} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                </div>
                <div className={classes.title}>Total Exposure by Client</div>
            </div>
            <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }}>
                {props.showAllRecords && (
                    <>
                        <div className={`${classes.scrollUp} scroll`}>
                            <IconButton size="medium" onClick={onScollBar('up')} ref={scrollUpAndPrevRef}>
                                <KeyboardArrowUpIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                        <div className={`${classes.scrollDown} scroll`}>
                            <IconButton size="medium" onClick={onScollBar('down')} ref={scrollDownAndNextRef}>
                                <KeyboardArrowDownIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                    </>
                )}
                <Bar
                    ref={chartRef}
                    onContextMenu={onContextMenu}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;
                            const item = state.groupedBy[_index];
                            const searchCriterias = new Array<SearchCriteria>();

                            searchCriterias.push({ property: 'Client', operator: 'eq', comparison: item });

                            props.getSelectedItem(searchCriterias, false, 'Client Exposure', DrillDownType.matterSummaryDrilldown, 'Matters');
                        }
                    }}
                    data={{
                        labels: getLabels(state.groupedBy),
                        datasets: [
                            {
                                label: 'Debtors',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary, true),
                                data: state.debtors,
                            },
                            {
                                label: 'Unbilled Fees',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.amber, true),
                                data: state.unbilledFees,
                            },
                            {
                                label: 'Unbilled Disbursement',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.red, true),
                                data: state.unbilledDisbursements,
                            },
                        ],
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick([...state.debtors, ...state.unbilledFees, ...state.unbilledDisbursements]),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit([...state.debtors, ...state.unbilledFees, ...state.unbilledDisbursements]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                                stacked: true
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                stacked: true,
                                min: 0,
                                max: props.showAllRecords ? 29 : 9,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: props.showAllRecords,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: props.showAllRecords,
                                    },
                                    onZoom: onZoom,
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: (context: any) => {
                                        const { dataIndex, formattedValue, dataset } = context;

                                        const currentValue = convertStringNumberToNumber(formattedValue);
                                        const amount = state.exposureTotal[dataIndex];
                                        const matterCount = state.matters[dataIndex];

                                        if (dataset.label.toLowerCase() === 'debtors') {
                                            return [
                                                `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`,
                                                `Unbilled Fees: ${state.symbol}${formatNumber(state.unbilledFees[dataIndex])}`,
                                                `Unbilled Disbursement: ${state.symbol}${formatNumber(state.unbilledDisbursements[dataIndex])}`,
                                                `Total Exposure: ${state.symbol}${formatNumber(amount)}`,
                                                `Matters: ${matterCount}`,
                                            ];
                                        } else if (dataset.label.toLowerCase() === 'unbilled fees') {
                                            return [
                                                `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`,
                                                `Debtors: ${state.symbol}${formatNumber(state.debtors[dataIndex])}`,
                                                `Unbilled Disbursement: ${state.symbol}${formatNumber(state.unbilledDisbursements[dataIndex])}`,
                                                `Total Exposure: ${state.symbol}${formatNumber(amount)}`,
                                                `Matters: ${matterCount}`,
                                            ];
                                        } else {
                                            return [
                                                `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`,
                                                `Unbilled Fees: ${state.symbol}${formatNumber(state.unbilledFees[dataIndex])}`,
                                                `Debtors: ${state.symbol}${formatNumber(state.debtors[dataIndex])}`,
                                                `Total Exposure: ${state.symbol}${formatNumber(amount)}`,
                                                `Matters: ${matterCount}`,
                                            ];
                                        }
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.debtors, state.unbilledFees, state.unbilledDisbursements) && (
                    <ChartEmptyState title="No client exposure" hint={"There are no debtors or unbilled amounts right now."} />
                )}
            </div>
            {state.groupedByCount > 10 && !props.showAllRecords && (
                <ChartLimitNote>The top 10 Clients are shown</ChartLimitNote>
            )}
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

const BILLED_VS_ESTIMATE_QUERY = gql`
    query ClientExposureData($entityType: Int, $entityGuid: String, $responsibility: Int, $showAllRecords: Boolean) {
        statistics {
            matter {
                clientExposure(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility, showAllRecords: $showAllRecords) {
                    total
                    groupedByCount
                    exposure {
                        debtors
                        unbilledFees
                        unbilledDisbursements
                        total
                        groupedBy
                        matters
                    }
                }
            }
        }
    }
`;
